import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlayerStatsInterface } from '../models/PlayerStats';
import { GoaliePlayerCard, PlayerCard, PlayerName } from './styles/TigersPlayerCard.Styles';


const formatPlayerName = (fullName: string, isMobile: boolean, number: number): string => {
    const names = fullName.split(' ');
    if (isMobile) {
      return names[names.length - 1];
    }
    // Keep only first and last name
    if (names.length > 2) {
      return `${names[0]} ${names[names.length - 1]}`;
    }
    return fullName;
};

interface ImageErrorState {
    [key: string]: boolean;
} 

export const TigersPlayerCard = (props: { player: PlayerStatsInterface, isMobile: boolean }) => {
    const navigate = useNavigate();
    const [imageErrors, setImageErrors] = useState<ImageErrorState>({});
    const handlePlayerClick = (playerName: string) => {
        navigate(`/player/${encodeURIComponent(playerName)}`);
    };
    const handleImageError = useCallback((playerName: string) => {
        setImageErrors(prev => {
            if (prev[playerName]) return prev;
            return { ...prev, [playerName]: true };
        });
        }, []);

    const isGoalie = props.player.position.split('/').some((pos) => pos.trim() === 'Goalie');

    const gamesPlayed = Number(props.player.gamesPlayed) || 0;

    // Goalie derived values (hide saves/shots in UI)
    const wins = Number(props.player.wins ?? 0) || 0;
    const losses = Number(props.player.losses ?? 0) || 0;
    const so = Number(props.player.so ?? 0) || 0;
    const ga = Number(props.player.ga ?? 0) || 0;

    const gaa =
        props.player.gaa !== undefined
          ? Number(props.player.gaa) || 0
          : gamesPlayed > 0
            ? ga / gamesPlayed
            : 0;

    const saves = Number(props.player.saves ?? 0) || 0;
    const shotsAgainst = Number(props.player.shotsAgainst ?? 0) || 0;
    const svRatioComputed = saves + shotsAgainst > 0 ? saves / (saves + shotsAgainst) : 0;

    const svRaw = props.player.svPercent !== undefined ? Number(props.player.svPercent) || 0 : svRatioComputed;
    // Backend might store ratio (0-1) or percent (0-100) - handle both.
    const svAsPercent = svRaw > 1 ? svRaw : svRaw * 100;

    if (isGoalie) {
        return (
            <GoaliePlayerCard key={props.player.player}>
                <div className="player-header">
                    <img
                        src={
                            imageErrors[props.player.player]
                                ? '/images/players/placeholder-player.jpg'
                                : `/images/players/${props.player.player.replace(/\s+/g, '-')}.png`
                        }
                        alt={props.player.player}
                        onError={() => handleImageError(props.player.player)}
                    />
                    <div className="player-info">
                        <PlayerName onClick={() => handlePlayerClick(props.player.player)}>
                            {formatPlayerName(props.player.player, props.isMobile, props.player.number)}
                            <span className="player-number">#{props.player.number || '0'}</span>
                        </PlayerName>
                        <div className="position">{props.player.position}</div>
                    </div>
                </div>

                <div className="stat">
                    <div className="value">{gamesPlayed}</div>
                    <div className="label">Games</div>
                </div>
                <div className="stat">
                    <div className="value">{wins}</div>
                    <div className="label">Wins</div>
                </div>
                <div className="stat">
                    <div className="value">{losses}</div>
                    <div className="label">Losses</div>
                </div>
                <div className="stat">
                    <div className="value">{so}</div>
                    <div className="label">SO</div>
                </div>
                <div className="stat">
                    <div className="value">{ga}</div>
                    <div className="label">GA</div>
                </div>
                <div className="stat">
                    <div className="value">{gaa.toFixed(2)}</div>
                    <div className="label">GAA</div>
                </div>
                <div className="stat">
                    <div className="value">{svAsPercent.toFixed(2)}%</div>
                    <div className="label">SV%</div>
                </div>
            </GoaliePlayerCard>
        );
    }

    // Skater (forwards/defence)
    return (
        <PlayerCard key={props.player.player}>
            <div className="player-header">
                <img
                    src={
                        imageErrors[props.player.player]
                            ? '/images/players/placeholder-player.jpg'
                            : `/images/players/${props.player.player.replace(/\s+/g, '-')}.png`
                    }
                    alt={props.player.player}
                    onError={() => handleImageError(props.player.player)}
                />
                <div className="player-info">
                    <PlayerName onClick={() => handlePlayerClick(props.player.player)}>
                        {formatPlayerName(props.player.player, props.isMobile, props.player.number)}
                        <span className="player-number">#{props.player.number || '0'}</span>
                    </PlayerName>
                    <div className="position">{props.player.position}</div>
                </div>
            </div>

            <div className="stat">
                <div className="value">{gamesPlayed}</div>
                <div className="label">Games</div>
            </div>
            <div className="stat">
                <div className="value">{Number(props.player.goals ?? 0) || 0}</div>
                <div className="label">Goals</div>
            </div>
            <div className="stat">
                <div className="value">{Number(props.player.assists ?? 0) || 0}</div>
                <div className="label">Assists</div>
            </div>
            <div className="stat">
                <div className="value">{Number(props.player.points ?? 0) || 0}</div>
                <div className="label">Points</div>
            </div>
            <div className="stat">
                <div className="value">{Number(props.player.ppg ?? 0).toFixed(2)}</div>
                <div className="label">PPG</div>
            </div>
            <div className="stat">
                <div className="value">{Number(props.player.pims ?? 0) || 0}</div>
                <div className="label">PIMS</div>
            </div>
        </PlayerCard>
    );
};